import api from "../../../services/api.service"

/**
 * Dev Tickets — internal issue / bug / feature / task tracker.
 * Org-scoped (no buids); pagination + sort are query params, filter is the body.
 * Backend: /dev-tickets/* (see DevTicketController).
 */
export const devTicketModule = {
    namespaced: true,
    state: {
        loading: false,
        // assignable users { id, name } cached for the participants picker
        assignableUsers: [],
    },
    getters: {
        isLoading: (state) => state.loading,
        getAssignableUsers: (state) => state.assignableUsers,
    },
    mutations: {
        setLoading(state, v) {
            state.loading = v;
        },
        setAssignableUsers(state, users) {
            state.assignableUsers = users || [];
        },
    },
    actions: {
        /** Paginated + filtered list. Returns the raw objects envelope. */
        fetchAll({commit, dispatch}, {filter, page, amount, sortby, sortcolumn} = {}) {
            commit('setLoading', true);
            return api.sendRequest('dev-tickets/get', {
                page,
                amount,
                sortby: sortby || 'desc',
                sortcolumn: sortcolumn || 'creationTime',
            }, filter || {}).then(
                (ans) => {
                    commit('setLoading', false);
                    return ans;
                },
                (error) => {
                    commit('setLoading', false);
                    dispatch('notifications/error', {
                        title: 'Dev Tickets',
                        message: (typeof error === 'object' && error.message !== undefined ? error.message : error),
                    }, {root: true});
                    return Promise.reject(error);
                }
            );
        },
        fetchById({dispatch}, id) {
            return api.get('dev-tickets/id/' + id).then(
                (ans) => ans['DevTicket'],
                (error) => {
                    dispatch('notifications/error', {
                        title: 'Dev Tickets',
                        message: (typeof error === 'object' && error.message !== undefined ? error.message : error),
                    }, {root: true});
                    return Promise.reject(error);
                }
            );
        },
        /** Create. When files are present, posts multipart; otherwise JSON. */
        create({dispatch}, {ticket, files}) {
            if (files && files.length) {
                const formData = new FormData();
                // plain string → bound as a text @RequestParam (a Blob would be
                // treated as a file part and fail String conversion)
                formData.append('ticket', JSON.stringify(ticket));
                files.forEach((f) => formData.append('files', f));
                return this.$api.uploadApi()
                    .post('dev-tickets/add', formData, {headers: authHeader()})
                    .then(
                        (res) => res.data.objects['DevTicket'],
                        (error) => rejectWith(dispatch, error)
                    );
            }
            return api.sendRequest('dev-tickets/add/json', null, ticket).then(
                (ans) => ans['DevTicket'],
                (error) => rejectWith(dispatch, error)
            );
        },
        update({dispatch}, {id, ticket}) {
            return api.sendRequest('dev-tickets/update/' + id, null, ticket).then(
                (ans) => ans['DevTicket'],
                (error) => rejectWith(dispatch, error)
            );
        },
        updateStatus({dispatch}, {id, status}) {
            return api.sendRequest('dev-tickets/update/status/' + id, {status}, {}).then(
                (ans) => ans['DevTicket'],
                (error) => rejectWith(dispatch, error)
            );
        },
        /** Add a comment, optionally with attachments (multipart). */
        addComment({dispatch}, {id, message, files}) {
            if (files && files.length) {
                const formData = new FormData();
                formData.append('comment', JSON.stringify({message}));
                files.forEach((f) => formData.append('files', f));
                return this.$api.uploadApi()
                    .post('dev-tickets/comment/' + id, formData, {headers: authHeader()})
                    .then(
                        (res) => res.data.objects['DevTicket'],
                        (error) => rejectWith(dispatch, error)
                    );
            }
            return api.sendRequest('dev-tickets/comment/' + id + '/json', null, {message}).then(
                (ans) => ans['DevTicket'],
                (error) => rejectWith(dispatch, error)
            );
        },
        /** Download an attachment as a blob and trigger a browser save. */
        downloadFile({dispatch}, {ticketId, fileName, originalName}) {
            return this.$api.downloadApi()
                .get('dev-tickets/file/' + ticketId + '/' + encodeURIComponent(fileName), {headers: authHeader()})
                .then(
                    (res) => {
                        const blob = new Blob([res.data]);
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', originalName || fileName);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        return true;
                    },
                    (error) => rejectWith(dispatch, error)
                );
        },
        /** Load org users for the participants picker (cached in state). */
        loadAssignableUsers({commit, dispatch, rootGetters}) {
            return new Promise((resolve) => {
                api.sendRequest('superuser/user/get', {
                    buids: (rootGetters['data/getSelectedBranchesIds'] || []).join(','),
                    amount: 1000,
                    page: 1,
                }, {}).then(
                    (ans) => {
                        let raw = ans && ans['Users'] ? ans['Users'] : [];
                        if (!Array.isArray(raw)) raw = Object.values(raw);
                        const users = raw.map((u) => ({
                            id: u.id,
                            name: ((u.firstname || '') + ' ' + (u.lastname || '')).trim()
                                || u.username || ('#' + u.id),
                        }));
                        commit('setAssignableUsers', users);
                        resolve(users);
                    },
                    () => resolve([])
                );
            });
        },
    }
};

function authHeader() {
    return {Authorization: 'Bearer ' + (localStorage.Bearer || '')};
}

function rejectWith(dispatch, error) {
    dispatch('notifications/error', {
        title: 'Dev Tickets',
        message: (typeof error === 'object' && error.message !== undefined ? error.message : error),
    }, {root: true});
    return Promise.reject(error);
}
